/**
 * Client-side YouTube ID extraction — fallback when API meta unavailable.
 */
export function extractYouTubeId(url) {
  if (!url) return null;
  try {
    // youtu.be/ID
    const short = url.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
    if (short) return short[1];

    const parsed = new URL(url);
    const host = parsed.hostname;
    if (!host.includes('youtube.com') && !host.includes('youtube-nocookie.com')) return null;

    // /watch?v=ID
    const v = parsed.searchParams.get('v');
    if (v && v.length === 11) return v;

    // /embed/ID, /v/ID, /shorts/ID
    for (const prefix of ['/embed/', '/v/', '/shorts/']) {
      if (parsed.pathname.startsWith(prefix)) {
        const segment = parsed.pathname.slice(prefix.length).split('/')[0];
        if (segment.length === 11) return segment;
      }
    }
  } catch {
    // invalid URL
  }
  return null;
}

/**
 * Build video metadata from a URL (client-side fallback).
 */
export function getVideoMeta(url) {
  if (!url || !url.trim()) return null;
  url = url.trim();

  const ytId = extractYouTubeId(url);
  if (ytId) {
    return {
      platform: 'youtube',
      video_id: ytId,
      embed_url: `https://www.youtube.com/embed/${ytId}`,
      thumbnail_url: `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`,
    };
  }

  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    const path = parsed.pathname.toLowerCase();

    // Uploaded video files in our own S3 bucket — playable inline via <video>.
    const MEDIA_HOSTS = [
      'prozpects-media.s3.us-east-1.amazonaws.com',
      'prozpects-media.s3.amazonaws.com',
    ];
    const VIDEO_EXTS = ['.mov', '.mp4', '.webm', '.m4v', '.ogg', '.ogv'];
    if (MEDIA_HOSTS.includes(host) && VIDEO_EXTS.some(ext => path.endsWith(ext))) {
      // Poster is a server-extracted first frame, proxied same-origin — shows a
      // thumbnail even for codecs the browser can't play inline (e.g. HEVC).
      return {
        platform: 'file',
        video_id: null,
        embed_url: url,
        thumbnail_url: `/api/athletes/video-thumbnail?url=${encodeURIComponent(url)}`,
      };
    }

    if (host.includes('hudl.com')) {
      // Mirror the backend: thumbnail is proxied same-origin so it renders even
      // when the caller doesn't pass server-computed meta.
      return {
        platform: 'hudl',
        video_id: null,
        embed_url: null,
        thumbnail_url: `/api/athletes/video-thumbnail?url=${encodeURIComponent(url)}`,
      };
    }
  } catch {
    // invalid URL
  }

  return { platform: 'other', video_id: null, embed_url: null, thumbnail_url: null };
}
